/**
 * PublicLayout - Layout for public content pages
 *
 * Used for SEO-optimized pages like:
 * - /arsredovisning (and subpages)
 * - /aktiebok
 * - /priser
 * - /om-oss
 * - /vanliga-fragor
 * - /villkor
 * - /integritetspolicy
 * - /kontakt
 */

import { Link, Outlet } from "react-router-dom";
import logoSvg from "@/assets/logo.svg";
import { PublicHeader } from "@/components/layout/PublicHeader";

// ═══════════════════════════════════════════════════════════════════
// Footer with SEO-optimized link structure
// ═══════════════════════════════════════════════════════════════════

function PublicFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-50 border-t border-gray-200" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Link columns - 1 col on xs, 2 on sm, 4 on md+ */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
          {/* Column 1: Produkter */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Produkter</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/arsredovisning"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Årsredovisning
                </Link>
              </li>
              <li>
                <Link
                  to="/arsredovisning/k2"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  K2 Årsredovisning
                </Link>
              </li>
              <li>
                <Link
                  to="/arsredovisning/k3"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  K3 Årsredovisning
                </Link>
              </li>
              <li>
                <Link
                  to="/aktiebok"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Digital aktiebok
                </Link>
              </li>
              <li>
                <Link
                  to="/priser"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Priser
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 2: Resurser */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Resurser</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/arsredovisning/guider"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Guider & artiklar
                </Link>
              </li>
              <li>
                <Link
                  to="/arsredovisning/guider/forsta-arsredovisningen"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Första årsredovisningen
                </Link>
              </li>
              <li>
                <Link
                  to="/arsredovisning/guider/k2-vs-k3"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  K2 vs K3
                </Link>
              </li>
              <li>
                <Link
                  to="/arsredovisning/guider/sie-fil"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  SIE-fil guide
                </Link>
              </li>
              <li>
                <Link
                  to="/vanliga-fragor"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Vanliga frågor
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Företag */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Företag</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/om-oss"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Om Kvanten
                </Link>
              </li>
              <li>
                <Link
                  to="/kontakt"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Juridiskt */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Juridiskt</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/villkor"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Användarvillkor
                </Link>
              </li>
              <li>
                <Link
                  to="/integritetspolicy"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Integritetspolicy
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Cookies
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Logo & Copyright - stack on mobile */}
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
              <img src={logoSvg} alt="Kvanten" className="h-6 w-auto" />
              <p className="text-xs sm:text-sm text-gray-500">
                © {currentYear} Kvanten. Alla rättigheter förbehållna.
              </p>
            </div>

            {/* Made in Sweden */}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
              <span>Utvecklat i Sverige</span>
              <svg className="w-5 h-4" viewBox="0 0 20 16" fill="none" aria-hidden="true">
                <rect width="20" height="16" fill="#006AA7" />
                <rect x="6" width="2.5" height="16" fill="#FECC00" />
                <rect y="6.5" width="20" height="3" fill="#FECC00" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Breadcrumbs Component
// ═══════════════════════════════════════════════════════════════════

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="flex items-center gap-2 text-sm text-gray-500">
        <li>
          <Link to="/" className="hover:text-gray-900 transition-colors">
            Hem
          </Link>
        </li>
        {items.map((item, index) => (
          <li key={index} className="flex items-center gap-2">
            <span aria-hidden="true">/</span>
            {item.href ? (
              <Link to={item.href} className="hover:text-gray-900 transition-colors">
                {item.label}
              </Link>
            ) : (
              <span className="text-gray-900">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Layout Export
// ═══════════════════════════════════════════════════════════════════

export function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
}

// Export individual components for flexibility
export { PublicFooter };
